import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { canRead, type Role } from '@/lib/permissions';
import type { ExpenseStatus, Tables, Updates } from '@/types/database';

/**
 * Hooks for the Expense Tracking feature.
 *
 * Visibility model (DB-enforced via RLS in supabase/migrations/21_*.sql):
 *   - super_admin sees every transaction in the org
 *   - everyone else sees only the transactions whose card_id maps to them
 *     via the card_assignments table
 *
 * Write model (DB-enforced via tg_transactions_employee_field_guard):
 *   - non-super_admin can only mutate expense_explanation, qb_account_id,
 *     receipt_path, receipt_uploaded_at, expense_status (draft <-> submitted)
 *   - approval (expense_status -> approved/rejected) is super_admin only
 *   - QBO writeback fields (qb_post_status etc.) are super_admin only
 */

const RECEIPTS_BUCKET = 'receipts';

function useExpensesEnabled(): boolean {
  const { role, loading } = useAuth();
  return !loading && canRead(role as Role | null, 'expenses');
}

/**
 * Embedded card + bank-account context the Expense pages need to render
 * cardholder, last-4, and the parent account name. Mirrors the join shape
 * the super_admin Transactions page uses so we can reuse the same UI bits.
 */
export type ExpenseTransaction = Tables<'transactions'> & {
  bank_accounts: Pick<Tables<'bank_accounts'>, 'id' | 'name' | 'account_type'> | null;
  cards: Pick<
    Tables<'cards'>,
    'id' | 'last_four' | 'name_on_card' | 'network'
  > | null;
  chart_of_accounts: Pick<
    Tables<'chart_of_accounts'>,
    'id' | 'name' | 'account_type' | 'code'
  > | null;
};

/**
 * Lists the transactions the current user is allowed to see for expense
 * reporting. RLS does the filtering — this hook just adds embedded joins
 * for the table view.
 *
 * params.scope:
 *   'mine' (default) — only transactions tied to a card the user owns. For
 *                      super_admin, this is identical to 'all' (super_admin
 *                      doesn't have card_assignments rows).
 *   'all'            — every transaction in the org. Super_admin only; for
 *                      other roles RLS will silently filter to their cards.
 */
export function useExpenseTransactions(params?: {
  limit?: number;
  scope?: 'mine' | 'all';
  status?: ExpenseStatus | 'all';
}) {
  const enabled = useExpensesEnabled();
  const status = params?.status ?? 'all';
  return useQuery({
    queryKey: ['expense-transactions', { ...params, status }],
    enabled,
    queryFn: async () => {
      let q = supabase
        .from('transactions')
        .select(
          '*, bank_accounts(id, name, account_type), cards(id, last_four, name_on_card, network), chart_of_accounts(id, name, account_type, code)',
        )
        .eq('direction', 'debit')
        .order('posted_at', { ascending: false })
        .limit(params?.limit ?? 200);
      if (status !== 'all') q = q.eq('expense_status', status);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as ExpenseTransaction[];
    },
  });
}

/** Fetch a single expense transaction with the same embedded joins as the list. */
export function useExpenseTransaction(id: string | undefined) {
  const enabled = useExpensesEnabled() && !!id;
  return useQuery({
    queryKey: ['expense-transaction', id],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transactions')
        .select(
          '*, bank_accounts(id, name, account_type), cards(id, last_four, name_on_card, network), chart_of_accounts(id, name, account_type, code)',
        )
        .eq('id', id!)
        .single();
      if (error) throw error;
      return data as unknown as ExpenseTransaction;
    },
  });
}

/**
 * Set the expense explanation. Anything else on the transaction is left
 * alone. The DB trigger blocks identity-column edits from non-super_admin,
 * so passing only `expense_explanation` is the safe pattern.
 */
export function useUpdateExpenseExplanation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; expense_explanation: string | null }) => {
      const { data, error } = await supabase
        .from('transactions')
        .update({ expense_explanation: input.expense_explanation })
        .eq('id', input.id)
        .select('id, expense_explanation')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['expense-transactions'] });
      qc.invalidateQueries({ queryKey: ['expense-transaction', variables.id] });
      // The explanation is a first-class input to the AI categoriser on
      // the Transaction Detail page. Drop the cached suggestion so the
      // panel re-runs with the new explanation in the prompt.
      qc.invalidateQueries({ queryKey: ['qb-category-ai-suggestion', variables.id] });
    },
  });
}

/** Pick (or clear) the QBO chart-of-accounts row this expense posts to. */
export function useUpdateExpenseCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; qb_account_id: string | null }) => {
      const { data, error } = await supabase
        .from('transactions')
        .update({ qb_account_id: input.qb_account_id })
        .eq('id', input.id)
        .select('id, qb_account_id, qb_post_status')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['expense-transactions'] });
      qc.invalidateQueries({ queryKey: ['expense-transaction', variables.id] });
    },
  });
}

/**
 * Move a draft expense into 'submitted'. The DB trigger only allows this
 * transition (and the reverse) from non-super_admin; super_admin can use
 * `useApproveExpense` / `useRejectExpense` to advance further.
 *
 * We require an explanation, a category, and a receipt before submission —
 * those are the three things the super_admin needs to actually approve. The
 * UI gates the button on the same condition; this is a defensive check so a
 * direct API call can't bypass it.
 */
export function useSubmitExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const { data: existing, error: fetchErr } = await supabase
        .from('transactions')
        .select('id, expense_explanation, qb_account_id, receipt_path, expense_status')
        .eq('id', input.id)
        .single();
      if (fetchErr) throw fetchErr;
      if (!existing) throw new Error('Transaction not found');
      if (existing.expense_status !== 'draft' && existing.expense_status !== 'rejected') {
        throw new Error(
          `Can't submit a ${existing.expense_status} expense. Ask the approver to reopen it first.`,
        );
      }
      if (!existing.expense_explanation || existing.expense_explanation.trim().length === 0) {
        throw new Error('Add an explanation before submitting.');
      }
      if (!existing.qb_account_id) {
        throw new Error('Pick a category before submitting.');
      }
      if (!existing.receipt_path) {
        throw new Error('Upload a receipt before submitting.');
      }

      const { data, error } = await supabase
        .from('transactions')
        .update({
          expense_status: 'submitted',
          submitted_at: new Date().toISOString(),
        })
        .eq('id', input.id)
        .select('id, expense_status, submitted_at')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['expense-transactions'] });
      qc.invalidateQueries({ queryKey: ['expense-transaction', variables.id] });
    },
  });
}

/**
 * Pull a submitted expense back to draft so the employee can fix it and
 * resubmit. Available to the employee themselves (since draft <-> submitted
 * is a permitted transition) and to super_admin.
 */
export function useUnsubmitExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const { data, error } = await supabase
        .from('transactions')
        .update({
          expense_status: 'draft',
          submitted_at: null,
        })
        .eq('id', input.id)
        .select('id, expense_status, submitted_at')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['expense-transactions'] });
      qc.invalidateQueries({ queryKey: ['expense-transaction', variables.id] });
    },
  });
}

/** Super_admin: approve a submitted expense. Trigger flips qb_post_status to pending. */
export function useApproveExpense() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const { data, error } = await supabase
        .from('transactions')
        .update({
          expense_status: 'approved',
          approved_by: user?.id ?? null,
          approved_at: new Date().toISOString(),
          rejection_reason: null,
        })
        .eq('id', input.id)
        .select('id, expense_status, approved_at, qb_post_status')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['expense-transactions'] });
      qc.invalidateQueries({ queryKey: ['expense-transaction', variables.id] });
      qc.invalidateQueries({ queryKey: ['transactions'] }); // shared with super_admin txns page
    },
  });
}

/**
 * Super_admin override: force expense_status to any value with the right
 * side-effects on the workflow timestamps and the rejection reason.
 *
 * This is the engine behind the inline Status picker on the Expense
 * Tracking and Transactions list views. Super_admin needs the ability to
 * push a row backwards (e.g. approved -> draft after a categorisation
 * mistake) or sideways (e.g. draft -> approved on their own card receipts)
 * without dancing through submit/approve. The DB-level guard in
 * tg_transactions_employee_field_guard already enforces super_admin-only
 * for any transition outside draft<->submitted, so this hook just bundles
 * the right column writes for each target status.
 *
 * Side-effects per target status:
 *   draft     -> wipe submitted_at, approved_at, approved_by, rejection_reason
 *   submitted -> set submitted_at = now, wipe approved_* and rejection_reason
 *   approved  -> set approved_at = now, approved_by = current user, wipe rejection_reason
 *   rejected  -> requires `reason`; sets rejection_reason and approved_*
 *   in_qb     -> records who marked it + when via approved_by/approved_at so
 *                the audit trail captures the "already in QB" decision the
 *                same way it does an approval. wipes rejection_reason.
 */
export function useSetExpenseStatus() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      status: ExpenseStatus;
      reason?: string;
    }) => {
      const now = new Date().toISOString();
      const updates: Updates<'transactions'> = { expense_status: input.status };
      switch (input.status) {
        case 'draft':
          updates.submitted_at = null;
          updates.approved_at = null;
          updates.approved_by = null;
          updates.rejection_reason = null;
          break;
        case 'submitted':
          updates.submitted_at = now;
          updates.approved_at = null;
          updates.approved_by = null;
          updates.rejection_reason = null;
          break;
        case 'approved':
          updates.approved_at = now;
          updates.approved_by = user?.id ?? null;
          updates.rejection_reason = null;
          break;
        case 'rejected': {
          const reason = (input.reason ?? '').trim();
          if (!reason) throw new Error('Rejection reason required.');
          updates.rejection_reason = reason;
          updates.approved_at = now;
          updates.approved_by = user?.id ?? null;
          break;
        }
        case 'in_qb':
          updates.approved_at = now;
          updates.approved_by = user?.id ?? null;
          updates.rejection_reason = null;
          break;
      }
      const { data, error } = await supabase
        .from('transactions')
        .update(updates)
        .eq('id', input.id)
        .select(
          'id, expense_status, submitted_at, approved_at, approved_by, rejection_reason, qb_post_status',
        )
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['expense-transactions'] });
      qc.invalidateQueries({ queryKey: ['expense-transaction', variables.id] });
      // The super_admin Transactions page renders the same rows; keep both
      // caches in sync so a status change on one view shows up on the other.
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['transaction', variables.id] });
    },
  });
}

/** Super_admin: reject a submitted expense. Records a reason for the employee. */
export function useRejectExpense() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: { id: string; reason: string }) => {
      const reason = input.reason.trim();
      if (!reason) throw new Error('Rejection reason required.');
      const { data, error } = await supabase
        .from('transactions')
        .update({
          expense_status: 'rejected',
          rejection_reason: reason,
          approved_by: user?.id ?? null,
          approved_at: new Date().toISOString(),
        })
        .eq('id', input.id)
        .select('id, expense_status, rejection_reason')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['expense-transactions'] });
      qc.invalidateQueries({ queryKey: ['expense-transaction', variables.id] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Build the canonical receipt object key for a transaction:
 *   <organization_id>/<transaction_id>/<filename>
 *
 * This shape is what the storage RLS policy (migration 22) decodes via
 * split_part(name, '/', N). Keeping the helper centralised so we can never
 * desync from the policy.
 */
export function buildReceiptPath(args: {
  organization_id: string;
  transaction_id: string;
  filename: string;
}): string {
  const safe = sanitizeReceiptFilename(args.filename);
  return `${args.organization_id}/${args.transaction_id}/${safe}`;
}

function sanitizeReceiptFilename(name: string): string {
  // Prepend a timestamp so re-uploads don't collide and the most recent file
  // sorts last. Keep alphanumerics and a small punctuation set; replace
  // anything else (spaces, slashes, …) with '_' so the whole key stays a
  // safe S3-style path.
  const stamp = Date.now();
  const cleaned = name.replace(/[^a-zA-Z0-9._-]+/g, '_');
  return `${stamp}-${cleaned}`;
}

/**
 * Upload a receipt for a transaction. We delete any prior receipt first so
 * the bucket doesn't accumulate stale files (we don't track receipt history
 * — the audit log already records the row mutation). Then we update the
 * transactions row to point at the new path.
 */
export function useUploadReceipt() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { transactionId: string; file: File; previousPath: string | null }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const path = buildReceiptPath({
        organization_id: profile.organization_id,
        transaction_id: input.transactionId,
        filename: input.file.name,
      });

      const { error: uploadErr } = await supabase.storage
        .from(RECEIPTS_BUCKET)
        .upload(path, input.file, {
          upsert: true,
          cacheControl: '3600',
          contentType: input.file.type || undefined,
        });
      if (uploadErr) throw uploadErr;

      // Best-effort cleanup of the prior receipt. We tolerate a failure here
      // (e.g. storage RLS rejection on a stale path that's already gone) so
      // the user never sees a successful upload paired with an "unable to
      // delete" toast.
      if (input.previousPath && input.previousPath !== path) {
        await supabase.storage.from(RECEIPTS_BUCKET).remove([input.previousPath]);
      }

      const { data, error: dbErr } = await supabase
        .from('transactions')
        .update({
          receipt_path: path,
          receipt_uploaded_at: new Date().toISOString(),
        })
        .eq('id', input.transactionId)
        .select('id, receipt_path, receipt_uploaded_at')
        .single();
      if (dbErr) throw dbErr;
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['expense-transactions'] });
      qc.invalidateQueries({ queryKey: ['expense-transaction', variables.transactionId] });
      qc.invalidateQueries({ queryKey: ['receipt-url', variables.transactionId] });
      // The super_admin Transactions page reads from the same row, so its
      // cache needs to refresh too — otherwise TransactionDetailPage keeps
      // showing the pre-upload state until a hard navigation.
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['transaction', variables.transactionId] });
    },
  });
}

/** Remove the current receipt and clear the pointer column. */
export function useRemoveReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { transactionId: string; path: string }) => {
      await supabase.storage.from(RECEIPTS_BUCKET).remove([input.path]);
      const { data, error } = await supabase
        .from('transactions')
        .update({
          receipt_path: null,
          receipt_uploaded_at: null,
        })
        .eq('id', input.transactionId)
        .select('id, receipt_path')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['expense-transactions'] });
      qc.invalidateQueries({ queryKey: ['expense-transaction', variables.transactionId] });
      qc.invalidateQueries({ queryKey: ['receipt-url', variables.transactionId] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['transaction', variables.transactionId] });
    },
  });
}

/**
 * Mint a short-lived signed URL for a receipt so the browser can render
 * the image / open the PDF. Uses 5 minutes which is plenty for a single
 * page view; the bucket is private so we never want a long-lived link.
 */
export function useReceiptSignedUrl(transactionId: string | undefined, path: string | null | undefined) {
  return useQuery({
    queryKey: ['receipt-url', transactionId, path],
    enabled: !!transactionId && !!path,
    staleTime: 4 * 60 * 1000,
    queryFn: async () => {
      if (!path) return null;
      const { data, error } = await supabase.storage
        .from(RECEIPTS_BUCKET)
        .createSignedUrl(path, 60 * 5);
      if (error) throw error;
      return data?.signedUrl ?? null;
    },
  });
}
